import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../LanguageContext';
import { illustrativeLabel, type Bi, type Line } from '../../data/storyPage';
export const EASE = [0.23, 1, 0.32, 1] as const;
export function usePick() {
  const {
    lang
  } = useLanguage();
  return (b: Bi) => lang === 'FR' ? b.fr : b.en;
}
export function Lines({
  lines,
  className = ''
}: {
  lines: Line[];
  className?: string;
}) {
  const pick = usePick();
  return <span className={className} data-id="element-6746" data-mp="30wmc21ou8g2p">
      {lines.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-6747" data-mp="30y76lrb5jz0k">
          {pick(l.text)}
        </span>)}
    </span>;
}
const HEADLINE_SIZES = {
  xl: 'text-[2.5rem] sm:text-[3.3rem] lg:text-[4.2rem] leading-[1.02]',
  lg: 'text-[2.1rem] sm:text-[2.7rem] lg:text-[3.3rem] leading-[1.06]',
  md: 'text-[1.9rem] sm:text-[2.3rem] lg:text-[2.6rem] leading-[1.1]'
};
export function StoryHeadline({
  id,
  lines,
  size = 'lg',
  as = 'h2',
  className = ''
}: {
  id?: string;
  lines: Line[];
  size?: keyof typeof HEADLINE_SIZES;
  as?: 'h1' | 'h2' | 'h3' | 'p';
  className?: string;
}) {
  const Tag = as;
  return <Tag id={id} className={`font-serif tracking-tight ${HEADLINE_SIZES[size]} ${className}`} data-id="element-6748" data-mp="2kdiv5n9ukb9x">
      <Lines lines={lines} data-id="element-6749" data-mp="2pdn9b6na8twz" />
    </Tag>;
}
export function Reveal({
  children,
  className = '',
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const reduce = useReducedMotion();
  return <motion.div initial={reduce ? false : {
    opacity: 0,
    y: 12
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.3,
    ease: EASE,
    delay
  }} className={className} data-id="element-6750" data-mp="1wp9bos03vzly">
      {children}
    </motion.div>;
}
export function StoryPhoto({
  src,
  alt,
  aspect = 'aspect-[4/3]',
  className = '',
  children
}: {
  src: string;
  alt: string;
  aspect?: string;
  className?: string;
  children?: React.ReactNode;
}) {
  const pick = usePick();
  return <figure className={`relative overflow-hidden rounded-2xl bg-[#F1F0FA] ${aspect} ${className}`} data-id="element-6751" data-mp="1i1nxpff3qtrc">
      <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-id="element-6752" data-mp="1d2ksnuz4gnjd" />
      {children}
      <figcaption className="absolute left-3 bottom-3 rounded-full bg-white/90 px-2.5 py-1 font-sans text-[11px] text-[#5A6178]" data-id="element-6753" data-mp="1m153k9yfxsx1">
        {pick(illustrativeLabel)}
      </figcaption>
    </figure>;
}
export const bodyText = 'font-sans text-[16px] md:text-[17px] leading-relaxed text-[#4A5168]';
export const eyebrowText = 'font-sans text-xs font-semibold tracking-[0.22em] uppercase text-[#4F46E5]';